import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiPlay, FiSquare, FiClock, FiTrendingUp, FiTrendingDown, FiActivity, FiCheckCircle, FiXCircle, FiLoader } from 'react-icons/fi';
import BacktestConfigPanel from '../../Backtesting/components/BacktestConfigPanel';
import BacktestTradeLog from '../../Backtesting/components/BacktestTradeLog';
import BacktestPeriodAnalysis from '../../Backtesting/components/BacktestPeriodAnalysis';
import BacktestAnalyticsPanel from '../../Backtesting/components/BacktestAnalyticsPanel';
import BacktestPerformance from '../../Backtesting/components/BacktestPerformance';
import { services } from '../../../../../services/api';
import { getDatePartsInTimezone } from '../../../../../utils/timezone';

const BacktestStepRight = ({
    strategyId,
    config,
    setConfig,
    onRun,
    onLoadRun,
    isRunning,
    progress,
    results,
    logs,
    dateRange,
    setSelectedTimeRange,
    onBack,
    isMetricsExpanded,
    onToggleExpanded,
    exchangeTimezone = 'Asia/Kolkata'
}) => {
    const [activeTab, setActiveTab] = useState('config');
    const [subTab, setSubTab] = useState('settings');
    const [runs, setRuns] = useState([]);
    const [isLoadingRuns, setIsLoadingRuns] = useState(false);

    // Fetch previous runs when tab is opened
    useEffect(() => {
        if (activeTab === 'runs' && strategyId && runs.length === 0) {
            fetchRuns();
        }
    }, [activeTab, strategyId]);

    const fetchRuns = async () => {
        setIsLoadingRuns(true);
        try {
            const resp = await services.backtesting.listRuns({ strategy_id: strategyId, limit: 10 });
            const data = resp.data?.data || resp.data;
            setRuns(data?.runs || []);
        } catch (e) {
            console.error("Failed to load past runs", e);
        } finally {
            setIsLoadingRuns(false);
        }
    };

    const handleRunClick = (run) => {
        onLoadRun(run);
        setActiveTab('config');
        setSubTab('trades');
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '—';
        // --> Format in exchange timezone to avoid browser-local date shifts
        const dp = getDatePartsInTimezone(dateStr, exchangeTimezone);
        if (!dp) return '—';
        const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
        return `${MONTHS[dp.month]} ${dp.day}, ${dp.year}`;
    };

    return (
        <div className="h-full flex flex-col justify-between">
            <div className="space-y-4 flex-1 flex flex-col overflow-hidden">
                {/* Tabs */}
                <div className="flex flex-col gap-3 pb-3 border-b border-[var(--divider)] shrink-0">
                    <div className="flex bg-bg-3 dark:bg-bg-2/50 p-1 rounded-lg">
                        <button
                            onClick={() => { setActiveTab('config'); setSubTab('settings'); }}
                            className={`flex-1 text-xs font-bold py-1.5 px-1 rounded-md transition-all ${activeTab === 'config' ? 'bg-white dark:bg-bg-3 text-fg-1 shadow-sm' : 'text-fg-3 hover:text-fg-1'}`}
                        >
                            Configuration
                        </button>
                        <button
                            onClick={() => setActiveTab('runs')}
                            className={`flex-1 text-xs font-bold py-1.5 px-1 rounded-md transition-all ${activeTab === 'runs' ? 'bg-white dark:bg-bg-3 text-fg-1 shadow-sm' : 'text-fg-3 hover:text-fg-1'}`}
                        >
                            History
                        </button>
                    </div>

                    {/* Secondary Tabs for Config when results exist */}
                    {activeTab === 'config' && results && (
                        <div className="flex bg-white dark:bg-bg-2 border border-[var(--divider)] p-1 rounded-lg shrink-0 shadow-sm">
                            <button
                                onClick={() => setSubTab('settings')}
                                className={`flex-1 text-[11px] font-bold py-1 px-1 rounded transition-all ${subTab === 'settings' ? 'bg-bg-3 text-fg-1' : 'text-fg-3 hover:text-fg-1'}`}
                            >
                                Settings
                            </button>
                            <button
                                onClick={() => setSubTab('trades')}
                                className={`flex-1 text-[11px] font-bold py-1 px-1 rounded transition-all ${subTab === 'trades' ? 'bg-bg-3 text-fg-1' : 'text-fg-3 hover:text-fg-1'}`}
                            >
                                Trade Logs
                            </button>
                            <button
                                onClick={() => setSubTab('periods')}
                                className={`flex-1 text-[11px] font-bold py-1 px-1 rounded transition-all ${subTab === 'periods' ? 'bg-bg-3 text-fg-1' : 'text-fg-3 hover:text-fg-1'}`}
                            >
                                Period Analysis
                            </button>
                            <button
                                onClick={() => setSubTab('performance')}
                                className={`flex-1 text-[11px] font-bold py-1 px-1 rounded transition-all ${subTab === 'performance' ? 'bg-bg-3 text-fg-1' : 'text-fg-3 hover:text-fg-1'}`}
                            >
                                Performance
                            </button>
                            <button
                                onClick={() => setSubTab('analytics')}
                                className={`flex-1 text-[11px] font-bold py-1 px-1 rounded transition-all ${subTab === 'analytics' ? 'bg-bg-3 text-fg-1' : 'text-fg-3 hover:text-fg-1'}`}
                            >
                                Analytics
                            </button>
                        </div>
                    )}
                </div>
                
                <div className="flex-1 overflow-y-auto pr-1 pb-2 flex flex-col gap-4">
                     {/* Dynamic Content */}
                     <AnimatePresence mode="wait">
                         {activeTab === 'config' && (!results || subTab === 'settings') && (
                             <motion.div
                                key="config"
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.2 }}
                                className="flex flex-col gap-4 h-full"
                             >
                                <BacktestConfigPanel
                                    config={config}
                                    setConfig={setConfig}
                                    dateRange={dateRange}
                                />
                             </motion.div>
                         )}
                         {activeTab === 'config' && results && subTab === 'trades' && (
                            <motion.div
                                key="trades"
                                initial={{ opacity: 0, x: 10 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.2 }}
                                className="flex flex-col h-full bg-white dark:bg-bg-2 rounded-xl border border-[var(--divider)] shadow-inner overflow-hidden"
                            >
                                <BacktestTradeLog trades={results.trades} logs={logs} exchangeTimezone={exchangeTimezone} onTradeSelect={setSelectedTimeRange} />
                            </motion.div>
                         )}
                         {activeTab === 'config' && results && subTab === 'periods' && (
                            <motion.div
                                key="periods"
                                initial={{ opacity: 0, x: 10 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.2 }}
                                className="flex flex-col h-full bg-white dark:bg-bg-2 rounded-xl border border-[var(--divider)] shadow-inner overflow-hidden"
                            >
                                <BacktestPeriodAnalysis 
                                    trades={results.trades} 
                                    onPeriodSelect={setSelectedTimeRange}
                                    exchangeTimezone={exchangeTimezone}
                                />
                            </motion.div>
                         )}
                         {activeTab === 'config' && results && subTab === 'performance' && (
                            <motion.div
                                key="performance"
                                initial={{ opacity: 0, x: 10 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.2 }}
                                className="flex flex-col h-full bg-white dark:bg-bg-2 rounded-xl border border-[var(--divider)] shadow-inner overflow-hidden"
                            >
                                <BacktestPerformance
                                    metrics={results?.metrics}
                                    trades={results?.trades}
                                    initialCapital={results?.initial_capital || config.initialCapital}
                                    startDate={config.startDate}
                                    endDate={config.endDate}
                                    sideFilter="ALL"
                                />
                            </motion.div>
                         )}
                         {activeTab === 'config' && results && subTab === 'analytics' && (
                            <motion.div
                                key="analytics"
                                initial={{ opacity: 0, x: 10 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.2 }}
                                className="flex flex-col h-full bg-white dark:bg-bg-2 rounded-xl border border-[var(--divider)] shadow-inner overflow-hidden"
                            >
                                <BacktestAnalyticsPanel
                                    metrics={results?.metrics}
                                    chartData={results?.chartData}
                                    buyHoldData={results?.buyHoldCurve}
                                    initialCapital={results?.initial_capital}
                                    strategyId={strategyId}
                                />
                            </motion.div>
                         )}
                         {activeTab === 'runs' && (
                             <motion.div
                                key="runs"
                                initial={{ opacity: 0, x: 10 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.2 }}
                                className="flex flex-col gap-3"
                             >
                                 {isLoadingRuns ? (
                                    <div className="text-center py-10">
                                        <FiLoader className="animate-spin text-fg-3 mx-auto w-6 h-6 mb-2" />
                                        <p className="text-xs text-fg-3">Loading historical runs...</p>
                                    </div>
                                 ) : runs.length === 0 ? (
                                    <div className="text-center py-10 bg-bg-3 dark:bg-bg-2/50 rounded-xl border border-dashed border-[var(--divider)]">
                                        <p className="text-xs text-fg-3 font-medium">No previous runs found.</p>
                                    </div>
                                 ) : (
                                    runs.map(run => {
                                        const m = run.metrics || {};
                                        const ret = m.total_return_pct ?? null;
                                        const isPos = ret >= 0;
                                        return (
                                            <div 
                                                key={run.run_id} 
                                                onClick={() => handleRunClick(run)}
                                                className="bg-white dark:bg-bg-2 border border-[var(--divider)] rounded-xl p-4 cursor-pointer hover:shadow-md hover:border-[var(--color-accent)] transition-all flex flex-col gap-3 group"
                                            >
                                                <div className="flex justify-between items-start">
                                                    <div>
                                                        <div className="flex items-center gap-2 mb-1">
                                                            <span className={`w-2 h-2 rounded-full ${run.status === 'completed' ? 'bg-[var(--badge-profit-dot)]' : run.status === 'failed' ? 'bg-[var(--badge-loss-dot)]' : 'bg-[var(--badge-neutral-dot)]'}`}></span>
                                                            <p className="text-xs font-bold text-fg-1">
                                                                {formatDate(run.start_date)} - {formatDate(run.end_date)}
                                                            </p>
                                                        </div>
                                                        <p className="text-[10px] text-fg-3 ml-4 font-mono">
                                                            {run.timeframe} • {run.instrument_id || 'ANY'}
                                                        </p>
                                                    </div>
                                                    <div className={`px-3 py-1.5 rounded-lg text-xs font-bold font-mono ${ret != null ? (isPos ? 'bg-profit/10 text-profit' : 'bg-loss/10 text-loss') : 'bg-bg-3 text-fg-3'}`}>
                                                        {ret != null ? `${isPos ? '+' : ''}${ret.toFixed(2)}%` : '—'}
                                                    </div>
                                                </div>
                                                
                                                {/* Metrics Row */}
                                                <div className="flex gap-4 ml-4 pt-2 border-t border-[var(--divider)]">
                                                    <div className="flex flex-col">
                                                        <span className="text-[9px] uppercase tracking-wider text-fg-3 font-bold mb-0.5">Trades</span>
                                                        <span className="text-[11px] font-mono font-bold text-fg-1">{m.total_trades ?? '—'}</span>
                                                    </div>
                                                    <div className="flex flex-col">
                                                        <span className="text-[9px] uppercase tracking-wider text-fg-3 font-bold mb-0.5">Win Rate</span>
                                                        <span className="text-[11px] font-mono font-bold text-fg-1">{m.win_rate != null ? `${m.win_rate.toFixed(1)}%` : '—'}</span>
                                                    </div>
                                                    <div className="flex flex-col">
                                                        <span className="text-[9px] uppercase tracking-wider text-fg-3 font-bold mb-0.5">Sharpe</span>
                                                        <span className="text-[11px] font-mono font-bold text-fg-1">{m.sharpe_ratio != null ? m.sharpe_ratio.toFixed(2) : '—'}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })
                                 )}
                             </motion.div>
                         )}
                     </AnimatePresence>
                </div>
            </div>
        </div>
    );
};

export default BacktestStepRight;
